import { useState } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { RiMailLine, RiMessage2Line, RiWhatsappLine } from "react-icons/ri";
import StatsCard from "../components/common/StatsCard";

const Analytics = () => {
  const [timeRange, setTimeRange] = useState("7d");

  const stats = [
    {
      title: "Emails Sent",
      value: "12,543",
      icon: RiMailLine,
      color: "primary",
    },
    {
      title: "SMS Sent",
      value: "5,271",
      icon: RiMessage2Line,
      color: "success",
    },
    {
      title: "WhatsApp Messages",
      value: "3,829",
      icon: RiWhatsappLine,
      color: "warning",
    },
  ];

  const messageData = [
    { name: "Jan", emails: 4000, sms: 2400, whatsapp: 2400 },
    { name: "Feb", emails: 3000, sms: 1398, whatsapp: 2210 },
    { name: "Mar", emails: 2000, sms: 9800, whatsapp: 2290 },
    { name: "Apr", emails: 2780, sms: 3908, whatsapp: 2000 },
  ];

  const deliveryData = [
    { name: "Delivered", value: 85 },
    { name: "Bounced", value: 10 },
    { name: "Failed", value: 5 },
  ];

  const engagementData = [
    { name: "Opened", value: 68 },
    { name: "Clicked", value: 42 },
    { name: "Replied", value: 15 },
    { name: "Unengaged", value: 25 },
  ];

  const detailedEngagementData = [
    { date: "2023-10-01", opens: 120, clicks: 45 },
    { date: "2023-10-02", opens: 132, clicks: 62 },
    { date: "2023-10-03", opens: 101, clicks: 48 },
    { date: "2023-10-04", opens: 134, clicks: 70 },
    { date: "2023-10-05", opens: 90, clicks: 40 },
  ];

  const channelData = [
    { name: "Email", sent: 1200, delivered: 1150, engaged: 450 },
    { name: "SMS", sent: 800, delivered: 790, engaged: 600 },
    { name: "WhatsApp", sent: 500, delivered: 495, engaged: 400 },
  ];

  const COLORS = ["#0066FF", "#10B981", "#F59E0B", "#EF4444"];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-secondary-900">
          Analytics Overview
        </h1>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="input-field max-w-40"
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat) => (
          <StatsCard key={stat.title} {...stat} />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Message Volume</h2>
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={messageData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="emails" stroke="#0066FF" />
                <Line type="monotone" dataKey="sms" stroke="#10B981" />
                <Line type="monotone" dataKey="whatsapp" stroke="#F59E0B" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Engagement Rates</h2>
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={engagementData}
                  cx="50%"
                  cy="50%"
                  innerRadius={80}
                  outerRadius={120}
                  fill="#8884d8"
                  paddingAngle={5}
                  dataKey="value"
                >
                  {engagementData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Delivery Status</h2>
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={deliveryData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  label
                >
                  {deliveryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Detailed Engagement</h2>
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={detailedEngagementData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="opens" stroke="#0066FF" />
                <Line type="monotone" dataKey="clicks" stroke="#10B981" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="card">
        <h2 className="text-xl font-semibold mb-4">Channel Performance</h2>
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={channelData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="sent" fill="#0066FF" />
              <Bar dataKey="delivered" fill="#10B981" />
              <Bar dataKey="engaged" fill="#F59E0B" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
